/**
 * @file WmsDocumentsListBody.tsx
 * @description Тело реестра складских документов: общий список
 * `ResponsiveList` + «Показать ещё» / «Показаны все: N» (phase19.31, зона
 * «Склад»; SC46 — таблица от 1024, компактный список до 1024).
 *
 * Один на приёмки, перемещения и списания: страницы раньше держали три
 * самодельные таблицы с карточками (`ReceiptsTable`, `TransfersTable`,
 * `WriteoffsTable`) и три копии «Показать ещё» с ручными высотами. Страница
 * передаёт колонки, тексты пустых состояний и данные хука
 * `useWmsDocumentsList`; заготовки, ошибка с «Повторить» и пустые состояния —
 * забота `ResponsiveList`.
 */

import { Button } from "../../ui/button";
import { ResponsiveList } from "../../ui/ResponsiveList";
import type { ListColumn, ListEmptySpec, ListFilteredEmptySpec } from "../../ui/responsive-list-types";

/** Что от хука `useWmsDocumentsList` нужно телу реестра. */
export interface DocumentsListState<T> {
  items: T[];
  total: number;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
  reload: () => void;
}

interface Props<T> {
  list: DocumentsListState<T>;
  columns: ListColumn<T>[];
  rowKey: (row: T) => string;
  rowHref: (row: T) => string;
  rowLabel: (row: T) => string;
  /** «Реестр приёмок» — подпись таблицы для скринридера. */
  ariaLabel: string;
  /** Список пуст из-за отбора, а не потому что документов нет. */
  filtered: boolean;
  empty: ListEmptySpec;
  filteredEmpty: ListFilteredEmptySpec;
  /** «Показаны все приёмки» — к ней добавится число. */
  allShownText: string;
}

export function WmsDocumentsListBody<T>({
  list,
  columns,
  rowKey,
  rowHref,
  rowLabel,
  ariaLabel,
  filtered,
  empty,
  filteredEmpty,
  allShownText,
}: Props<T>) {
  return (
    <div className="flex flex-col gap-3">
      <ResponsiveList
        aria-label={ariaLabel}
        rows={list.items}
        columns={columns}
        rowKey={rowKey}
        rowHref={rowHref}
        rowLabel={rowLabel}
        rowSize="double"
        tableMinWidth="min-w-[920px]"
        loading={list.loading}
        error={list.error}
        onRetry={list.reload}
        filtered={filtered}
        empty={empty}
        filteredEmpty={filteredEmpty}
        skeletonRows={8}
      />
      {list.items.length === 0 ? null : list.hasMore ? (
        <Button variant="outline" className="self-center" onClick={list.loadMore} loading={list.loadingMore}>
          Показать ещё
        </Button>
      ) : (
        // Подпись под списком лежит на фоне кабинета — основным цветом (SC69).
        <p className="type-caption text-center text-on-surface">
          {allShownText}: <span className="tabular-nums">{list.total}</span>
        </p>
      )}
    </div>
  );
}
